import React, { useEffect, useState } from "react";
import MainLayoutWithoutFooter from "../components/MainLayoutWithoutFooter";
import AddPromotionModal from "../components/modals/AddPromotionModal";
import AuthorizationPinModal from "../components/modals/AuthorizationPinModal";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";

const Promotions = () => {
  const [promos, setPromos] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    pageCount: 1,
    perPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // The authorizing PIN (a manager's staff PIN or the restaurant's super admin PIN), held in
  // memory only for as long as the Add Promotion modal is open. The server is the authority: it
  // validates the PIN on create-offer, so this screen only collects it.
  const [authPin, setAuthPin] = useState(null);
  const [pinPromptOpen, setPinPromptOpen] = useState(false);
  const [pinError, setPinError] = useState("");

  const fetchPromotions = async (page = 1) => {
    setLoading(true);

    try {
      const url = `${apiUrls.PROMO_LISTING}?page=${page}`;
      const res = await callAPI(url,{}, "GET");
      console.log(res);
      const data = res.data || {};

      setPromos(data.data || []);
      setMeta(
        data._meta || { currentPage: 1, pageCount: 1, perPage: 20 }
      );
    } catch {
      alert("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions(meta.currentPage);
  }, [meta.currentPage]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setAuthPin(null);
  };

  // Everyone is asked for a PIN on every press of Add New - restaurants with staff PIN login off
  // too, and managers already signed in too - because the server needs a PIN in the create request
  // and does not trust this client's say-so about who is on the till. The PIN is NOT pre-checked
  // here: the staff-PIN endpoint only knows employee PINs and would wrongly reject a super admin
  // PIN, so the prompt just collects it and the server's answer (403 + message) is shown as-is.
  const handleAddClick = () => {
    setAuthPin(null);
    setPinError("");
    setPinPromptOpen(true);
  };

  const handlePinEntered = (pin) => {
    setPinPromptOpen(false);
    setAuthPin(pin);
    setShowAddModal(true);
  };

  // The server refused the PIN on save: keep the form, say why, and ask for the PIN again.
  const handlePinRejected = (message) => {
    setAuthPin(null);
    setPinError(message || "");
    setPinPromptOpen(true);
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent customersPage pb-3">
        <div className="container">

          {/* HEADER */}
          <div className="sbRow mb-3">
            <h2 className="lgTitle mb-0">Promotions</h2>

            <button
              type="button"
              className="btn iconBtn btnPrimary"
              onClick={handleAddClick}
            >
              Add New
            </button>
          </div>

          {/* TABLE */}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <div className="cardStyle">
              <table className="table tableStyle2">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Promo Code</th>
                    <th>Type</th>
                    <th>One-Time Use</th>
                    <th>Description</th>
                  </tr>
                </thead>

                <tbody>
                  {promos.length ? (
                    promos.map((p, i) => (
                      <tr key={i}>
                        <td>
                          {(meta.currentPage - 1) * meta.perPage + i + 1}
                        </td>
                        <td>{p.promocode}</td>
                        <td>{p.is_coupon === 1 ? "Voucher" : "Promo"}</td>
                        <td>{p.one_time_use_only === 1 ? "Yes" : "No"}</td>
                        <td>{p.details}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No promotions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* PAGINATION */}
              <div className="d-flex justify-content-center gap-3 mt-3">
                <button
                  disabled={meta.currentPage === 1}
                  onClick={() =>
                    setMeta((p) => ({
                      ...p,
                      currentPage: p.currentPage - 1,
                    }))
                  }
                >
                  Prev
                </button>

                <span>
                  Page {meta.currentPage} of {meta.pageCount}
                </span>

                <button
                  disabled={meta.currentPage === meta.pageCount}
                  onClick={() =>
                    setMeta((p) => ({
                      ...p,
                      currentPage: p.currentPage + 1,
                    }))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddPromotionModal
        isOpen={showAddModal}
        onClose={closeAddModal}
        onCreated={() => fetchPromotions(1)}
        authPin={authPin}
        onPinRejected={handlePinRejected}
      />
      <AuthorizationPinModal
        isOpen={pinPromptOpen}
        onCancel={() => setPinPromptOpen(false)}
        onConfirm={handlePinEntered}
        title="Authorization Required"
        subtitle="Enter a manager PIN or the restaurant super admin PIN to add a promotion"
        error={pinError}
      />
    </MainLayoutWithoutFooter>
  );
};

export default Promotions;